import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import closeIcon from '../Assets/images/close_red.png';
import axiosInstance from '../api/axiosInstance';

const SupportChatDialog = ({ open, handleClose }) => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [open]);

  const handleSendQuestion = async () => {
    if (!question.trim() || loading) return;

    const userQuestion = question.trim();
    setQuestion('');
    setError('');
    
    // Add user message to chat
    const newMessages = [...messages, { type: 'user', content: userQuestion }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_AIML_API_URL || 'http://localhost:5001';
      const response = await axiosInstance.post(
        `${apiUrl}/api/support-chat`,
        { 
          question: userQuestion
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const { answer } = response.data;

      // Add assistant response
      setMessages([
        ...newMessages,
        {
          type: 'assistant',
          content: answer || 'No answer available',
        },
      ]);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to get answer';
      setError(errorMsg);
      setMessages([
        ...newMessages,
        {
          type: 'error',
          content: `Error: ${errorMsg}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleResetHistory = () => {
    setMessages([]);
    setError('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuestion();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          height: '70vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6">Support Chat</Typography>
        <Box>
          <IconButton
            onClick={handleResetHistory}
            size="small"
            title="Clear Chat"
            sx={{ mr: 1 }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={handleClose} size="small">
            <img
              src={closeIcon}
              alt="close"
              style={{ height: '35px', cursor: 'pointer' }}
            />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#f5f5f5',
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
            }}
          >
            <Typography variant="h6" gutterBottom>
              How can I help you today?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Ask me questions about:
            </Typography>
            <Box component="ul" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography component="li" variant="body2">
                How to reset password
              </Typography>
              <Typography component="li" variant="body2">
                How to create a new lead
              </Typography>
              <Typography component="li" variant="body2">
                Product information
              </Typography>
            </Box>
          </Box>
        )}

        <Stack spacing={2}>
          {messages.map((message, index) => (
            <Box key={index}>
              {message.type === 'user' ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    <Typography variant="body1">{message.content}</Typography>
                  </Paper>
                </Box>
              ) : message.type === 'error' ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {message.content}
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                  Thinking...
                </Typography>
              </Paper>
            </Box>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '10px 20px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: 'white',
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ask a question..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          inputRef={inputRef}
          size="small"
          sx={{ mr: 1 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendQuestion}
          disabled={!question.trim() || loading}
          startIcon={<SendIcon />}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportChatDialog;

